import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import PageLayout from "../components/PageLayout";
import ServiceCard from "../components/ServiceCard";
import { industries } from "../data/industries";
import { services } from "../data/services";

const businessStages = [
  {
    label: "For Small Businesses",
    title: "Build credibility",
    description:
      "Ideal for businesses that need a professional website, basic branding, social media visuals, and a clear online presence.",
    items: ["Business website", "Contact forms", "Service pages", "Basic SEO setup"],
    highlighted: false
  },
  {
    label: "For Growing Companies",
    title: "Generate better leads",
    description:
      "Best for companies that already have customers but need better campaigns, landing pages, and conversion-focused design.",
    items: ["Landing pages", "Marketing assets", "Lead capture flows", "Analytics integration"],
    highlighted: true
  },
  {
    label: "For Larger Companies",
    title: "Scale operations",
    description:
      "Designed for teams that need scalable design systems, dashboards, automation, workflow tools, and custom online solutions.",
    items: ["UI/UX systems", "Internal dashboards", "Workflow automation", "Custom web tools"],
    highlighted: false
  }
];

const capabilityItems = [
  "Business discovery and digital planning",
  "Website and landing page production",
  "Brand and marketing asset creation",
  "Custom web systems and automation"
];

const processSteps = [
  {
    step: "Step 01",
    title: "Discovery",
    description:
      "We understand your business, target customers, current challenges, competitors, and what kind of online solution you actually need."
  },
  {
    step: "Step 02",
    title: "Strategy",
    description:
      "We define the pages, content direction, features, design style, and priorities before moving into production."
  },
  {
    step: "Step 03",
    title: "Design & Build",
    description:
      "We create the visual design, develop the website or assets, test responsiveness, and prepare everything for review."
  },
  {
    step: "Step 04",
    title: "Launch & Improve",
    description:
      "We help launch, monitor, refine, and support your online presence so your digital assets continue to work for your business."
  }
];

const engagementModels = [
  {
    title: "Starter Presence",
    description: "For businesses that need to look professional online quickly.",
    items: ["Website structure", "Homepage and service pages", "Basic branding direction", "Contact form setup"],
    cta: "Inquire",
    highlighted: false
  },
  {
    title: "Growth Partner",
    description: "For businesses that need continuous design, marketing, and web support.",
    items: ["Website improvements", "Landing pages", "Social media assets", "Monthly design support"],
    cta: "Recommended",
    highlighted: true
  },
  {
    title: "Custom Solutions",
    description: "For companies that need custom systems, dashboards, and automation.",
    items: ["Web applications", "Internal tools", "Dashboard UI/UX", "Workflow automation"],
    cta: "Discuss Project",
    highlighted: false
  }
];

const testimonials = [
  {
    quote:
      "They helped us understand what our business needed online without overwhelming us with technical terms.",
    name: "Small Business Owner",
    role: "Home Services"
  },
  {
    quote:
      "The process was simple. We got a cleaner brand, better website, and marketing materials we could actually use.",
    name: "Marketing Lead",
    role: "Growing Company"
  },
  {
    quote:
      "They helped translate our internal ideas into a clean digital system that made our workflow easier.",
    name: "Operations Manager",
    role: "Corporate Team"
  }
];

const industryButtonStyle = {
  shadow: "rgba(37,99,235,0.16)"
};


function HomePage() {
  const location = useLocation();
  const heroGlowRef = useRef(null);
  const heroCardRef = useRef(null);
  const heroStatsRef = useRef([]);
  const heroProgressRef = useRef(null);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const targetId = location.hash.replace("#", "");

    window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [location.hash]);

  useEffect(() => {
    const stats = heroStatsRef.current.filter(Boolean);
    const ctx = gsap.context(() => {
      if (heroGlowRef.current) {
        gsap.to(heroGlowRef.current, {
          yPercent: -3,
          xPercent: 2,
          scale: 1.03,
          opacity: 0.9,
          duration: 4.8,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true
        });
      }

      if (heroCardRef.current) {
        gsap.to(heroCardRef.current, {
          y: -10,
          rotate: -1.25,
          duration: 4.2,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true
        });
      }

      if (stats.length) {
        gsap.to(stats, {
          y: -8,
          duration: 2.8,
          ease: "sine.inOut",
          stagger: {
            each: 0.18,
            repeat: -1,
            yoyo: true
          },
          repeat: -1,
          yoyo: true
        });
      }

      if (heroProgressRef.current) {
        gsap.fromTo(
          heroProgressRef.current,
          { scaleX: 0.76, transformOrigin: "left center" },
          {
            scaleX: 0.84,
            duration: 2.6,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <PageLayout ctaHref="/booking">
      <section className="relative overflow-hidden hero-backdrop min-h-screen" aria-label="Modern office workspace background for Nucleonsoft digital agency">
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="absolute inset-0 soft-grid opacity-15"></div>
        <div className="absolute top-0 right-0 h-[600px] w-[600px] rounded-full bg-blue-400 opacity-20 blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 h-[500px] w-[500px] rounded-full bg-cyan-300 opacity-15 blur-3xl -translate-x-1/3 translate-y-1/3"></div>

        <div className="relative z-10 grid min-h-screen max-w-7xl mx-auto items-center gap-12 px-6 py-20 md:py-24 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <div>
            <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 shadow-sm backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-blue-400"></span>
              <span className="text-sm font-light text-white/80">Digital agency for modern businesses</span>
            </div>

            <h1 className="text-5xl font-extralight tracking-tight leading-[1.05] text-white md:text-7xl">
              Need to turn website visitors into leads? <span className="font-normal text-blue-400">We got your back.</span>
            </h1>

            <p className="mt-7 max-w-xl text-lg font-normal leading-8 text-slate-100 md:text-xl">
              We build websites that do more than look good — they help businesses organize inquiries, track interest, and respond faster.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/booking"
                className="inline-flex justify-center rounded-full bg-blue-600 px-8 py-4 font-light text-white shadow-lg shadow-blue-950/40 transition hover:bg-blue-500"
              >
                Discovery Call
              </Link>
              <a
                href="#services"
                className="inline-flex justify-center rounded-full border border-white/20 bg-white/10 px-8 py-4 font-light text-white backdrop-blur-sm transition hover:border-blue-300 hover:text-blue-100"
              >
                Explore Services
              </a>
            </div>

            <div className="mt-9 grid max-w-lg grid-cols-3 gap-6">
              <div>
                <h3 className="text-3xl font-extralight text-white">360°</h3>
                <p className="mt-2 text-sm font-light text-white/65">Online solutions</p>
              </div>
              <div>
                <h3 className="text-3xl font-extralight text-white">B2B</h3>
                <p className="mt-2 text-sm font-light text-white/65">Business focused</p>
              </div>
              <div>
                <h3 className="text-3xl font-extralight text-white">Growth</h3>
                <p className="mt-2 text-sm font-light text-white/65">Lead driven</p>
              </div>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[500px] opacity-90 lg:max-w-[480px]">
            <div
              ref={heroGlowRef}
              className="absolute -inset-5 rotate-3 rounded-[3rem] bg-gradient-to-br from-blue-400/30 to-cyan-300/20 blur-sm"
            ></div>
            <div
              ref={heroCardRef}
              className="relative rounded-[2rem] border border-white/25 bg-white/90 p-5 shadow-2xl shadow-slate-950/25 backdrop-blur-md will-change-transform"
            >
              <div className="flex items-center justify-between border-b border-slate-200/80 pb-4">
                <div>
                  <p className="text-xs font-light text-slate-400">Business Dashboard</p>
                  <h3 className="text-lg font-light text-slate-900">Digital Growth Overview</h3>
                </div>
                <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-light text-emerald-600">Active</div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div
                  ref={(element) => {
                    heroStatsRef.current[0] = element;
                  }}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-4 will-change-transform"
                >
                  <p className="text-sm font-light text-slate-400">Website Leads</p>
                  <h4 className="mt-3 text-3xl font-extralight">+42%</h4>
                </div>
                <div
                  ref={(element) => {
                    heroStatsRef.current[1] = element;
                  }}
                  className="rounded-2xl bg-blue-600 p-4 text-white will-change-transform"
                >
                  <p className="text-sm font-light text-blue-100">Brand Reach</p>
                  <h4 className="mt-3 text-3xl font-extralight">+68%</h4>
                </div>
                <div
                  ref={(element) => {
                    heroStatsRef.current[2] = element;
                  }}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-4 will-change-transform"
                >
                  <p className="text-sm font-light text-slate-400">Campaign Assets</p>
                  <h4 className="mt-3 text-3xl font-extralight">120+</h4>
                </div>
                <div
                  ref={(element) => {
                    heroStatsRef.current[3] = element;
                  }}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-4 will-change-transform"
                >
                  <p className="text-sm font-light text-slate-400">Automation</p>
                  <h4 className="mt-3 text-3xl font-extralight">24/7</h4>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-slate-100 p-4">
                <div className="mb-3 flex justify-between">
                  <span className="text-sm font-light text-slate-500">Project Progress</span>
                  <span className="text-sm font-light text-blue-600">82%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    ref={heroProgressRef}
                    className="h-full w-[82%] rounded-full bg-blue-600 will-change-transform"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="industries" className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">Industries</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight md:text-6xl">
              Flexible for different types of businesses.
            </h2>
          </div>

          <div className="grid gap-x-8 gap-y-9 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((industry) => {
              const tone = industryButtonStyle;
              return (
                <Link
                  key={industry.id}
                  to={industry.path}
                  className="group mx-auto flex w-full max-w-[295px] flex-col overflow-hidden rounded-xl bg-white text-white transition duration-300 hover:-translate-y-1"
                  style={{
                    boxShadow: `0 6px 0 ${tone.shadow}, 0 18px 34px ${tone.shadow}`
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.boxShadow = `0 8px 0 ${tone.shadow}, 0 24px 44px ${tone.shadow}`;
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.boxShadow = `0 6px 0 ${tone.shadow}, 0 18px 34px ${tone.shadow}`;
                  }}
                >
                  <div className="h-36 w-full overflow-hidden bg-slate-200">
                    <img
                      src={industry.heroImageUrl}
                      alt={industry.heroImageAlt || industry.name}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex h-[58px] w-full items-center justify-between border-t border-slate-100 bg-white text-slate-700 transition duration-300 group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                    <span className="flex-1 px-5 text-center text-sm font-light">{industry.name}</span>
                    <span
                      className="flex h-full w-16 items-center justify-center bg-slate-50 text-slate-700 transition duration-300 group-hover:bg-blue-700 group-hover:text-white"
                      style={{ clipPath: "polygon(24% 0, 100% 0, 100% 100%, 0 100%)" }}
                    >
                      <ArrowUpRight size={19} strokeWidth={2.2} className="transition duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-28 bg-white">
        <div className="grid max-w-7xl mx-auto items-start gap-20 px-6 lg:grid-cols-2">
          <div>
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">What We Do</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight leading-tight md:text-6xl">
              We turn business goals into clear digital experiences.
            </h2>
          </div>

          <div className="space-y-6">
            <p className="text-lg font-light leading-8 text-slate-500">
              Many businesses know they need a better online presence, but they do not always know where to start.
              We help simplify the process by planning, designing, building, and improving the right digital
              solutions based on your business stage.
            </p>

            <p className="text-lg font-light leading-8 text-slate-500">
              Whether you need a professional website, landing page, brand identity, marketing materials, online
              booking flow, customer form, or custom web system, we help you launch with clarity and confidence.
            </p>

            <p className="text-lg font-light leading-8 text-slate-500">
              If you are comparing website design packages, planning a digital marketing campaign, or looking for a
              quote for a custom web application, we can help you map the right scope before production starts.
            </p>

            <div className="rounded-[1.5rem] border border-slate-100 bg-slate-50 p-6">
              <p className="text-sm font-light uppercase tracking-[0.24em] text-blue-600">Service Area</p>
              <p className="mt-3 text-lg font-light leading-8 text-slate-500">
                We serve businesses in California and across the United States, with remote digital agency support
                for website design, landing pages, branding, marketing assets, and custom online systems.
              </p>
              <Link
                to="/california-digital-agency"
                className="mt-5 inline-flex text-sm font-light text-blue-600 transition hover:text-blue-500"
              >
                View California services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-16">
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">Services</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight md:text-6xl">
              Website design, landing pages, branding, and custom web systems.
            </h2>
            <p className="mt-6 max-w-2xl text-lg font-light leading-8 text-slate-500">
              Choose the service that fits your next step, then book a discovery call so we can recommend a practical
              website, campaign, or online system plan for your business.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                highlighted={service.id === "custom-online-systems"}
                animatedHover={service.id !== "custom-online-systems"}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="solutions" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">Business Stages</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight md:text-6xl">
              Solutions based on where your business is now.
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {businessStages.map((stage) => (
              <div
                key={stage.title}
                className={`rounded-[2rem] p-8 ${
                  stage.highlighted ? "border border-blue-100 bg-blue-50" : "border border-slate-100 bg-white"
                }`}
              >
                <p className="mb-6 text-sm font-light text-blue-600">{stage.label}</p>
                <h3 className="text-3xl font-extralight">{stage.title}</h3>
                <p className="mt-5 font-light leading-7 text-slate-500">{stage.description}</p>
                <ul className="mt-8 space-y-4 font-light text-slate-500">
                  {stage.items.map((item) => (
                    <li key={item}>✓ {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 bg-slate-950 text-white">
        <div className="grid max-w-7xl mx-auto items-center gap-20 px-6 lg:grid-cols-2">
          <div>
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-300">Capabilities</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight leading-tight md:text-6xl">
              Design, development, and growth support under one team.
            </h2>
            <p className="mt-8 text-lg font-light leading-8 text-slate-300">
              Instead of hiring separate people for design, websites, branding, and digital assets, your business
              can work with one focused team that understands your goals from strategy to launch.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {capabilityItems.map((item, index) => (
              <div
                key={item}
                className={`rounded-3xl p-6 ${
                  index === 3 ? "bg-blue-600" : "border border-white/10 bg-white/5"
                }`}
              >
                <h3 className="text-4xl font-extralight">{`0${index + 1}`}</h3>
                <p className={`mt-8 font-light ${index === 3 ? "text-blue-50" : "text-slate-300"}`}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="process" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-16">
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">Process</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight md:text-6xl">
              A simple process that business owners can understand.
            </h2>
          </div>

          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <div
                key={step.step}
                className={`grid gap-8 border-t border-slate-100 pt-8 lg:grid-cols-[200px_1fr] ${
                  index === processSteps.length - 1 ? "border-b border-slate-100 pb-8" : ""
                }`}
              >
                <p className="font-light text-blue-600">{step.step}</p>
                <div>
                  <h3 className="text-3xl font-extralight">{step.title}</h3>
                  <p className="mt-4 max-w-3xl font-light leading-7 text-slate-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="packages" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-16">
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">Engagement Models</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight md:text-6xl">
              Start small, then scale as your business grows.
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {engagementModels.map((model) => (
              <div
                key={model.title}
                className={`rounded-[2rem] p-8 ${
                  model.highlighted
                    ? "border border-blue-100 bg-blue-600 text-white shadow-xl shadow-blue-100"
                    : "border border-slate-100 bg-white"
                }`}
              >
                <h3 className="text-2xl font-light">{model.title}</h3>
                <p className={`mt-4 font-light leading-7 ${model.highlighted ? "text-blue-50" : "text-slate-500"}`}>
                  {model.description}
                </p>

                <ul className={`mt-8 space-y-4 font-light ${model.highlighted ? "text-blue-50" : "text-slate-500"}`}>
                  {model.items.map((item) => (
                    <li key={item}>✓ {item}</li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className={`mt-8 inline-flex w-full justify-center rounded-full py-4 font-light transition ${
                    model.highlighted
                      ? "bg-white text-blue-600 hover:bg-blue-50"
                      : "border border-slate-200 text-slate-700 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {model.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-600">Client Experience</span>
            <h2 className="mt-6 text-4xl font-extralight tracking-tight md:text-6xl">
              Designed to feel professional, clear, and easy to work with.
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {testimonials.map((testimonial) => (
              <div key={testimonial.name} className="rounded-[2rem] border border-slate-100 bg-white p-8">
                <p className="font-light leading-7 text-slate-500">“{testimonial.quote}”</p>
                <div className="mt-8">
                  <h4 className="font-light text-slate-900">{testimonial.name}</h4>
                  <p className="mt-1 text-sm font-light text-slate-400">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 p-10 text-white md:p-16">
            <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-blue-600 opacity-30 blur-3xl translate-x-1/3 -translate-y-1/3"></div>

            <div className="relative max-w-3xl">
              <span className="text-sm font-light uppercase tracking-[0.3em] text-blue-300">Start Your Project</span>
              <h2 className="mt-6 text-4xl font-extralight tracking-tight leading-tight md:text-6xl">
                Let’s build a digital presence your business can be proud of.
              </h2>
              <p className="mt-6 text-lg font-light leading-8 text-slate-300">
                Tell us about your business, and we’ll help identify the best online solution to start with.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <a
                  href="/booking"
                  className="inline-flex justify-center rounded-full bg-blue-600 px-8 py-4 font-light text-white transition hover:bg-blue-500"
                >
                  Discovery Call
                </a>
                <a
                  href="#services"
                  className="inline-flex justify-center rounded-full border border-white/10 bg-white/10 px-8 py-4 font-light text-white transition hover:bg-white hover:text-slate-950"
                >
                  View Services
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

export default HomePage;
